import { GridCellKind } from '@glideapps/glide-data-grid';

import type {
  CustomCell,
  GridCell,
  TextCell,
} from '@glideapps/glide-data-grid';

import type { Row, ColumnId, Lang } from '../types';

import { isMenuCell, getMenuCellTemplate } from '../../CustomCell/Menu';
import { isDateCell, getDateCellTemplate } from '../../CustomCell/Date';
import { isTickerCell, getTickerCellTemplate } from '../../CustomCell/Ticker';
import {
  isSharePriceCell,
  getSharePriceCellTemplate,
} from '../../CustomCell/SharePrice';
import { isSharesCell, getSharesCellTemplate } from '../../CustomCell/Shares';
import {
  isTotalCostCell,
  getTotalCostCellTemplate,
} from '../../CustomCell/TotalCost';

import {
  isTransactionIdCell,
  getTransactionIdCellTemplate,
} from '../../CustomCell/TransactionId';

const resetCustomCell = (cell: CustomCell) => {
  if (isMenuCell(cell)) {
    return getMenuCellTemplate();
  }
  if (isDateCell(cell)) {
    return getDateCellTemplate({ lang: cell.data.lang });
  }
  if (isTickerCell(cell)) {
    return getTickerCellTemplate();
  }
  if (isSharePriceCell(cell)) {
    return getSharePriceCellTemplate();
  }
  if (isSharesCell(cell)) {
    return getSharesCellTemplate();
  }
  if (isTotalCostCell(cell)) {
    return getTotalCostCellTemplate();
  }
  if (isTransactionIdCell(cell)) {
    return getTransactionIdCellTemplate();
  }
  return cell;
};

export const resetCell = (cell: GridCell) => {
  switch (cell.kind) {
    case GridCellKind.Text: {
      return {
        ...cell,
        data: '',
        displayData: '',
      };
    }
    case GridCellKind.Number: {
      return {
        ...cell,
        data: 0,
        displayData: '',
      };
    }
    case GridCellKind.Custom: {
      return resetCustomCell(cell);
    }
  }
  return cell;
};

const defaultCell: TextCell = {
  kind: GridCellKind.Text,
  allowOverlay: true,
  readonly: false,
  displayData: '',
  data: '',
};

const emptyRow: Row = {
  ticker: {
    exchangeCode: null,
    symbol: null,
    companyName: null,
  },
  date: null,
  shares: null,
  price: null,
  cost: null,
  type: null,
  currencyISO: null,
  transactionId: null,
};

export const createCell = (
  columnId?: ColumnId,
  rowData: Row = emptyRow,
  lang?: Lang
) => {
  if (typeof columnId === 'undefined') return defaultCell;

  const value = rowData[columnId];
  const { currencyISO } = rowData;

  if (columnId === 'ticker') {
    const { exchangeCode, symbol, companyName } = rowData[columnId];
    const uniqueSymbol =
      exchangeCode && symbol ? `${exchangeCode}:${symbol}` : null;
    return getTickerCellTemplate({
      value: uniqueSymbol,
      name: companyName,
      status: exchangeCode && symbol ? 'success' : 'idle',
      uniqueSymbol,
      currencyISO,
    });
  }

  if (columnId === 'date') {
    return getDateCellTemplate({ value, lang });
  }

  if (columnId === 'type') {
    return getMenuCellTemplate({ value });
  }

  if (columnId === 'price') {
    return getSharePriceCellTemplate({ value, currencyISO });
  }

  if (columnId === 'shares') {
    return getSharesCellTemplate({ value });
  }

  if (columnId === 'cost') {
    return getTotalCostCellTemplate({ value, currencyISO });
  }

  if (columnId === 'transactionId') {
    return getTransactionIdCellTemplate({ value });
  }

  return defaultCell;
};
